import React from "react";
import { useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

import { i18n } from "../../translate/i18n";
import CardCounter from "../../components/Dashboard/CardCounter";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import AssessmentIcon from "@material-ui/icons/Assessment";
import WarningIcon from "@material-ui/icons/Warning";
import MoneyIcon from "@material-ui/icons/Money";

const heatColors = (v, max) => {
  if (!max || max <= 0) return "#f5f5f5";
  const t = Math.min(1, v / max);
  const r = Math.round(200 + 55 * (1 - t));
  const g = Math.round(220 * (1 - t));
  const b = Math.round(240);
  return `rgb(${r},${g},${b})`;
};

const TicketReportsExtended = ({ extended, loading }) => {
  const theme = useTheme();
  if (!extended || loading) return null;

  const { kpis, comparison, chartsExtra, tables, alerts, goals } = extended;
  const ts = chartsExtra?.timeSeriesDaily || [];
  const heat = chartsExtra?.heatmap || [];
  const stacked = chartsExtra?.stackedByWeek || [];
  const funnel = chartsExtra?.funnel || [];
  const byConn = chartsExtra?.byConnection || [];

  const heatMax = Math.max(0, ...heat.map((h) => h.value || 0));
  const hours = [...Array(24).keys()];
  const dows = [0, 1, 2, 3, 4, 5, 6];

  const stackKeys =
    stacked.length > 0
      ? Object.keys(stacked[0]).filter((k) => k !== "week")
      : [];

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("ticketReports.sectionAdvancedKpis")}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CardCounter
          icon={<AssessmentIcon fontSize="inherit" />}
          title={i18n.t("ticketReports.kpiSlaWait")}
          value={
            kpis?.slaWaitMetPct != null
              ? `${Number(kpis.slaWaitMetPct).toFixed(1)}%`
              : "—"
          }
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CardCounter
          icon={<AssessmentIcon fontSize="inherit" />}
          title={i18n.t("ticketReports.kpiSlaSupport")}
          value={
            kpis?.slaSupportMetPct != null
              ? `${Number(kpis.slaSupportMetPct).toFixed(1)}%`
              : "—"
          }
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CardCounter
          icon={<WarningIcon fontSize="inherit" />}
          title={i18n.t("ticketReports.kpiAbandonment")}
          value={
            kpis?.abandonmentRatePct != null
              ? `${Number(kpis.abandonmentRatePct).toFixed(1)}%`
              : "—"
          }
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CardCounter
          icon={<TrendingUpIcon fontSize="inherit" />}
          title={i18n.t("ticketReports.kpiFirstReply")}
          value={
            kpis?.avgFirstResponseMinutes != null
              ? `${Number(kpis.avgFirstResponseMinutes).toFixed(1)} min`
              : "—"
          }
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CardCounter
          icon={<AssessmentIcon fontSize="inherit" />}
          title={i18n.t("ticketReports.kpiRecurring")}
          value={
            kpis?.recurringContactsRatePct != null
              ? `${Number(kpis.recurringContactsRatePct).toFixed(1)}%`
              : "—"
          }
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <CardCounter
          icon={<MoneyIcon fontSize="inherit" />}
          title={i18n.t("ticketReports.kpiEstimatedCost")}
          value={
            kpis?.estimatedCost != null
              ? Number(kpis.estimatedCost).toFixed(2)
              : "—"
          }
          loading={loading}
        />
      </Grid>

      {comparison && (
        <Grid item xs={12}>
          <Paper style={{ padding: 16 }} variant="outlined">
            <Typography variant="subtitle1" gutterBottom>
              {i18n.t("ticketReports.sectionComparison")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("ticketReports.cmpTotal")}
                </Typography>
                <Typography variant="h6">
                  {comparison.totalTrackings}{" "}
                  {comparison.deltaTotalPct != null && (
                    <Chip
                      size="small"
                      icon={
                        comparison.deltaTotalPct >= 0 ? (
                          <TrendingUpIcon />
                        ) : (
                          <TrendingDownIcon />
                        )
                      }
                      label={`${comparison.deltaTotalPct >= 0 ? "+" : ""}${comparison.deltaTotalPct.toFixed(1)}%`}
                      color={comparison.deltaTotalPct >= 0 ? "primary" : "secondary"}
                    />
                  )}
                  <Typography component="span" variant="body2" color="textSecondary">
                    {" "}
                    ({i18n.t("ticketReports.cmpPrev")}: {comparison.prevTotalTrackings})
                  </Typography>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("ticketReports.cmpFinalized")}
                </Typography>
                <Typography variant="h6">
                  {comparison.finalizedTrackings}{" "}
                  {comparison.deltaFinalizedPct != null && (
                    <Chip
                      size="small"
                      label={`${comparison.deltaFinalizedPct >= 0 ? "+" : ""}${comparison.deltaFinalizedPct.toFixed(1)}%`}
                    />
                  )}
                  <Typography component="span" variant="body2" color="textSecondary">
                    {" "}
                    ({comparison.prevFinalizedTrackings})
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {alerts?.length > 0 && (
        <Grid item xs={12}>
          <Paper style={{ padding: 16 }} variant="outlined">
            <Typography variant="subtitle1" gutterBottom>
              {i18n.t("ticketReports.sectionAlerts")}
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {alerts.map((a, i) => (
                <Chip
                  key={i}
                  label={a.message}
                  color={a.severity === "warning" ? "secondary" : "default"}
                />
              ))}
            </div>
          </Paper>
        </Grid>
      )}

      {goals?.queuesAboveGoal?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("ticketReports.goalWaitLabel", {
              n: goals.goalWaitMinutes,
            })}
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("ticketReports.sectionTimeCharts")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper style={{ padding: 16 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("ticketReports.chartDailyVolume")}
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={ts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                name={i18n.t("ticketReports.chartVolume")}
                stroke={theme.palette.primary.main}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper style={{ padding: 16 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("ticketReports.chartFunnel")}
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill={theme.palette.secondary.main} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper style={{ padding: 16, overflowX: "auto" }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("ticketReports.chartHeatmap")}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>DOW \ H</TableCell>
                {hours.map((h) => (
                  <TableCell key={h} align="center" style={{ padding: 4, fontSize: 10 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dows.map((d) => (
                <TableRow key={d}>
                  <TableCell style={{ fontSize: 11 }}>{d}</TableCell>
                  {hours.map((h) => {
                    const cell = heat.find((x) => x.dow === d && x.hour === h);
                    const v = cell ? cell.value : 0;
                    return (
                      <TableCell
                        key={`${d}-${h}`}
                        align="center"
                        style={{
                          padding: 2,
                          fontSize: 9,
                          backgroundColor: heatColors(v, heatMax),
                          minWidth: 22,
                        }}
                      >
                        {v > 0 ? v : ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper style={{ padding: 16 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("ticketReports.chartStackedWeek")}
          </Typography>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={stacked}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {stackKeys.map((k, i) => (
                <Bar
                  key={k}
                  dataKey={k}
                  stackId="a"
                  fill={["#3f51b5", "#e91e63", "#00acc1", "#ff9800", "#4caf50"][i % 5]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 16 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("ticketReports.chartByConnection")}
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byConn}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill={theme.palette.primary.main} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          {i18n.t("ticketReports.sectionRankings")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 8 }} variant="outlined">
          <Typography variant="subtitle2">{i18n.t("ticketReports.tableAgents")}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("ticketReports.attendant")}</TableCell>
                <TableCell align="right">Vol.</TableCell>
                <TableCell align="right">TMA</TableCell>
                <TableCell align="right">NPS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tables?.rankingAgents || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.volume}</TableCell>
                  <TableCell align="right">
                    {r.avgSupportMinutes != null ? r.avgSupportMinutes.toFixed(1) : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {r.avgRating != null ? r.avgRating.toFixed(2) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 8 }} variant="outlined">
          <Typography variant="subtitle2">{i18n.t("ticketReports.tableQueues")}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("ticketReports.queue")}</TableCell>
                <TableCell align="right">Vol.</TableCell>
                <TableCell align="right">{i18n.t("ticketReports.avgWaitShort")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tables?.rankingQueues || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.volume}</TableCell>
                  <TableCell align="right">
                    {r.avgWaitMinutes != null ? r.avgWaitMinutes.toFixed(1) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 8 }} variant="outlined">
          <Typography variant="subtitle2">{i18n.t("ticketReports.tableConnections")}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("ticketReports.connection")}</TableCell>
                <TableCell align="right">Vol.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tables?.rankingConnections || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.volume}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 8 }} variant="outlined">
          <Typography variant="subtitle2">{i18n.t("ticketReports.tableTags")}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tag</TableCell>
                <TableCell align="right">Vol.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tables?.topTags || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper style={{ padding: 8 }} variant="outlined">
          <Typography variant="subtitle2">{i18n.t("ticketReports.tableRecurring")}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("ticketReports.contact")}</TableCell>
                <TableCell>{i18n.t("ticketReports.phone")}</TableCell>
                <TableCell align="right">Tickets</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tables?.recurringContacts || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.number}</TableCell>
                  <TableCell align="right">{r.ticketCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper style={{ padding: 8 }} variant="outlined">
          <Typography variant="subtitle2">{i18n.t("ticketReports.tableTagQueue")}</Typography>
          <div style={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tag</TableCell>
                  <TableCell>{i18n.t("ticketReports.queue")}</TableCell>
                  <TableCell align="right">Vol.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tables?.tagQueueMatrix || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.tagName}</TableCell>
                    <TableCell>{r.queueName}</TableCell>
                    <TableCell align="right">{r.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </Grid>
    </>
  );
};

export default TicketReportsExtended;
